import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { Search, ArrowRight } from 'lucide-react';
import type { WhatsAppTemplate, TemplateMessageComponent, TemplateComponent } from '@/types/whatsapp';
import { cn } from '@/lib/core/utils';

interface TemplateSelectorProps {
  templates: WhatsAppTemplate[];
  onSelect: (templateName: string, components?: TemplateMessageComponent[]) => void;
  onClose: () => void;
}

export function TemplateSelector({ templates, onSelect, onClose }: TemplateSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTemplateVariables = (template: WhatsAppTemplate): string[] => {
    const variables: string[] = [];
    const bodyComponent = template.components.find(c => c.type === 'BODY');

    if (bodyComponent?.text) {
      const matches = bodyComponent.text.match(/\{\{(\d+)\}\}/g);
      if (matches) {
        matches.forEach((_, index) => {
          variables.push(`{{${index + 1}}}`);
        });
      }
    }

    return variables;
  };

  const getTemplatePreview = (template: WhatsAppTemplate): string => {
    const bodyComponent = template.components.find(c => c.type === 'BODY');
    let text = bodyComponent?.text || 'No content';

    // Replace variables with entered values or placeholders
    Object.entries(variableValues).forEach(([key, value]) => {
      text = text.replace(key, value || key);
    });

    return text;
  };

  const handleSelectTemplate = (template: WhatsAppTemplate) => {
    setSelectedTemplate(template);
    // Initialize variable values
    const vars = getTemplateVariables(template);
    const initialValues: Record<string, string> = {};
    vars.forEach(v => {
      initialValues[v] = '';
    });
    setVariableValues(initialValues);
  };

  const handleSend = () => {
    if (!selectedTemplate) return;

    // Build template components with variable values
    const variables = Object.values(variableValues).filter(v => v);
    const components: TemplateMessageComponent[] = [];

    if (variables.length > 0) {
      components.push({
        type: 'body',
        parameters: variables.map(value => ({
          type: 'text',
          text: value,
        })),
      });
    }

    onSelect(selectedTemplate.name, components.length > 0 ? components : undefined);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'MARKETING':
        return 'bg-primary/10 text-primary';
      case 'UTILITY':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'AUTHENTICATION':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {selectedTemplate ? 'Configure Template' : 'Select a Template'}
          </DialogTitle>
          <DialogDescription>
            {selectedTemplate
              ? 'Fill in the variables and preview the message before sending'
              : 'Choose a pre-approved template to start or continue the conversation'
            }
          </DialogDescription>
        </DialogHeader>

        {!selectedTemplate ? (
          // Template list view
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <ScrollArea className="h-[400px]">
              {filteredTemplates.length === 0 ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  No templates found
                </div>
              ) : (
                <div className="space-y-2 pr-4">
                  {filteredTemplates.map(template => (
                    <Card
                      key={template.id}
                      className={cn(
                        'cursor-pointer transition-colors hover:bg-muted/50',
                      )}
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{template.name}</h4>
                              <Badge
                                className={cn('text-xs', getCategoryColor(template.category))}
                              >
                                {template.category}
                              </Badge>
                            </div>
                            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                              {getTemplatePreview(template)}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Language: {template.language.toUpperCase()}
                            </p>
                          </div>
                          <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </>
        ) : (
          // Template configuration view
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedTemplate(null)}
              >
                ← Back
              </Button>
              <Badge className={getCategoryColor(selectedTemplate.category)}>
                {selectedTemplate.category}
              </Badge>
              <span className="font-medium">{selectedTemplate.name}</span>
            </div>

            {/* Variable inputs */}
            {Object.keys(variableValues).length > 0 && (
              <div className="space-y-3">
                <Label>Fill in the variables:</Label>
                {Object.keys(variableValues).map((variable, index) => (
                  <div key={variable} className="space-y-1">
                    <Label className="text-sm text-muted-foreground">
                      Variable {index + 1}
                    </Label>
                    <Input
                      placeholder={`Enter value for ${variable}`}
                      value={variableValues[variable]}
                      onChange={(e) =>
                        setVariableValues(prev => ({
                          ...prev,
                          [variable]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Preview */}
            <div className="space-y-2">
              <Label>Preview:</Label>
              <div className="rounded-lg bg-green-600 p-4 text-white">
                <p className="whitespace-pre-wrap text-sm">
                  {getTemplatePreview(selectedTemplate)}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSend}>
                Send Template
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
